import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useFeatureGate from '../hooks/useFeatureGate';
import usePlayback from '../hooks/usePlayback';
import Header from '../components/Header';
import AdOverlay from '../components/AdOverlay';
import { useCollection } from '../hooks/useCollection';
import { formatDate, formatDuration } from '../utils/format';
import * as analytics from '../services/analytics';

function parseChannels(search) {
  const qp = new URLSearchParams(search);
  const raw = qp.get('channels') || qp.get('channel') || '';
  if (!raw) return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

function parseSelectedIds(search) {
  const qp = new URLSearchParams(search);
  const raw = qp.get('selectedIds') || '';
  /* c8 ignore start */
  if (!raw) return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
  /* c8 ignore stop */
}

export default function PlaybackPage() {
  // --- all hooks unconditionally at top level ---
  const { user } = useAuth();
  const globalEnabled = useFeatureGate('feature:favorites');
  const navigate = useNavigate();
  const location = useLocation();

  // Parse query params
  const channels = useMemo(() => parseChannels(location.search), [location.search]);
  const source = useMemo(() => {
    const qp = new URLSearchParams(location.search);
    /* c8 ignore next */
    /* c8 ignore next */
    return qp.get('source') || 'favorites';
  }, [location.search]);
  const selectedIds = useMemo(() => {
    /* c8 ignore next */
    return parseSelectedIds(location.search);
  }, [location.search]);
  
  // Get user's tier max days for clip date range
  const maxDays = user?.tier?.features?.search?.maxDays ?? 7;

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const iframeTimeoutRef = useRef(null);
  const iframeLoadedRef = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [shouldShowAd, setShouldShowAd] = useState(false);
  const [embedBlocked, setEmbedBlocked] = useState(false);
  const [adCheckPending, setAdCheckPending] = useState(false);
  const [autoPlayMode, setAutoPlayMode] = useState(true);
  const [failedAvatarUrls, setFailedAvatarUrls] = useState(new Set());

  const playback = usePlayback(
    channels, 
    50, 
    maxDays,
    { source, selectedIds }
  );
  const { addToCollection } = useCollection();

  // Determine clip type early for use in effects
  const clip = playback.currentClip;
  const isMp4 = clip?.url && /\.mp4(\?|$)/i.test(clip.url);

  // Stable refs for callbacks so the auto-advance effect doesn't re-run every
  // second just because the playback object has a new reference.
  const playNextRef = useRef(null);
  const setPlayStateRef = useRef(null);
  playNextRef.current = playback.playNext;
  setPlayStateRef.current = playback.setPlayState;

  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [added, setAdded] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);

  // Helper to build avatar URL using same proxy as FavoritesPage
  function channelAvatarUrl(slug) {
    /* c8 ignore start */
    if (!slug) return null;
    /* c8 ignore stop */
    return `https://unavatar.io/twitch/${encodeURIComponent(slug)}`;
  }

  // Find an avatar URL for a given channel slug by checking loaded clips first,
  // then falling back to the proxy avatar URL.
  function avatarForChannelSlug(slug) {
    /* c8 ignore start */
    if (!slug) return null;
    const found = playback.clips?.find((c) => {
      const candidate = (c.broadcaster_login || c.channelName || c.channel || c.channel_name || '').toString().toLowerCase();
      return candidate === slug.toLowerCase();
    });
    if (found && (found.profile_image_url || found.profileImageUrl)) return found.profile_image_url || found.profileImageUrl;
    return channelAvatarUrl(slug);
    /* c8 ignore stop */
  }

  // Robustly extract a channel/login slug from a clip object
  // PRIORITIZE broadcaster_login which is now sent from backend for all clips
  function extractChannelSlug(clipObj) {
    if (!clipObj) return '';
    const candidates = [
      clipObj.broadcaster_login,  // ← NEW: backend now sends this for all clips
      clipObj.broadcasterLogin,
      clipObj.channel,
      clipObj.channel_name,
      clipObj.channelName,
      clipObj.broadcaster_name,
      clipObj.broadcasterName,
      clipObj.user_login,
      clipObj.userLogin,
      clipObj.creatorName,
      clipObj.creator_name,
    ];
    for (const c of candidates) {
      if (c && typeof c === 'string') return c.toLowerCase();
    }

    // Fallback: parse the clip URL to extract channel path segment
    try {
      const u = new URL(clipObj.url);
      // URL formats: https://www.twitch.tv/CHANNEL/clip/SLUG or https://clips.twitch.tv/SLUG
      /* c8 ignore start */
      if (u.hostname && (u.hostname.includes('twitch.tv') || u.hostname.includes('clips.twitch.tv'))) {
        const parts = u.pathname.replace(/^\/+/, '').split('/');
        // parts[0] is channel for /CHANNEL/clip/SLUG
        if (parts.length > 0 && parts[0] !== 'clip' && parts[0] !== 'embed') return parts[0].toLowerCase();
      }
      /* c8 ignore stop */
    } catch (e) {
      // ignore
    }

    return '';
  }

  const playbackEnabled = user?.tier?.features?.playback?.enabled ?? false;

  // Sync local isPlaying with hook state
  useEffect(() => {
    setIsPlaying(playback.isPlaying);
  }, [playback.isPlaying]);

  // Track playback start when clips are loaded
  useEffect(() => {
    if (playback.clips && playback.clips.length > 0 && !playback.isLoading) {
      if (source === 'collection') {
        analytics.trackPlaybackStart(0, playback.clips.length);
      } else {
        analytics.trackPlaybackStart(channels.length, playback.clips.length);
      }
    }
  }, [playback.clips, playback.isLoading, channels.length, source]);

  // Reset embed blocked state and start a timeout when clip changes
  useEffect(() => {
    /* c8 ignore start */
    if (iframeTimeoutRef.current) {
      clearTimeout(iframeTimeoutRef.current);
      iframeTimeoutRef.current = null;
    }
    /* c8 ignore stop */
    iframeLoadedRef.current = false;
    setEmbedBlocked(false);

    const clip = playback.currentClip;
    if (!clip) return;

    if (clip.embedUrl && clip.embedUrl.includes('clips.twitch.tv')) {
      iframeTimeoutRef.current = setTimeout(() => {
        /* c8 ignore start */
        if (!iframeLoadedRef.current) {
          setEmbedBlocked(true);
          // Only pause if the user is in manual mode; in auto-play mode keep advancing
          if (!autoPlayMode && playback.setPlayState) playback.setPlayState(false);
        }
        /* c8 ignore stop */
      }, 4000);
    }

    return () => {
      /* c8 ignore start */
      if (iframeTimeoutRef.current) {
        clearTimeout(iframeTimeoutRef.current);
        iframeTimeoutRef.current = null;
      }
      /* c8 ignore stop */
    };
  }, [playback.currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // In Auto-Play mode, ensure isPlaying is true whenever a new clip becomes active
  useEffect(() => {
    if (autoPlayMode && playback.currentClip) {
      setIsPlaying(true);
      if (playback.setPlayState) playback.setPlayState(true);
    }
  }, [playback.currentIndex, autoPlayMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-advance to next clip when current clip duration expires.
  // For MP4s: rely on onEnded (accurate).
  // For Twitch embeds: timer MUST start from onIframeLoad, not from clip-change time,
  // because the iframe can take 2-5 seconds to load — starting the timer at clip-change
  // means firing that many seconds early, and the gap grows with each clip.
  const autoAdvanceRef = useRef(null);
  const autoPlayDurationMsRef = useRef(null); // stores intended delay; read by onIframeLoad

  useEffect(() => {
    // Clear any existing auto-advance timeout
    /* c8 ignore start */
    if (autoAdvanceRef.current) {
      clearTimeout(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
    /* c8 ignore stop */
    autoPlayDurationMsRef.current = null;

    const clip = playback.currentClip;
    if (!clip || !isPlaying || !autoPlayMode) return;
    if (isMp4) return; // MP4s use onEnded handler

    const durationSec = clip.duration;
    if (durationSec && durationSec > 0) {
      const delayMs = (durationSec * 1000) + 500;
      autoPlayDurationMsRef.current = delayMs;
      // If the iframe is already loaded (e.g., returning to this clip), start immediately.
      // Otherwise onIframeLoad will start the timer once the embed is ready.
      /* c8 ignore start */
      if (iframeLoadedRef.current) {
        autoAdvanceRef.current = setTimeout(() => {
          playNextRef.current?.(false);
          setPlayStateRef.current?.(true);
        }, delayMs);
      }
      /* c8 ignore stop */
    }

    return () => {
      /* c8 ignore start */
      if (autoAdvanceRef.current) {
        clearTimeout(autoAdvanceRef.current);
        autoAdvanceRef.current = null;
      }
      /* c8 ignore stop */
    };
  }, [playback.currentClip, isPlaying, autoPlayMode, isMp4]); // eslint-disable-line react-hooks/exhaustive-deps


  // Note: fullscreen is handled by the embedded Twitch player; do not manage it here.

  // Check whether to show an ad after auto-advancing
  // This runs whenever the clip index changes via auto-advance (not manual navigation)
  useEffect(() => {
    async function checkAndTriggerAd() {
      if (!user?.tier?.features?.playback?.ad_enabled) {
        return;
      }

      if (adCheckPending) return;
      setAdCheckPending(true);

      try {
        // Only check for ads if the last navigation was automatic (not manual)
        if (playback.lastNavigationManual && playback.lastNavigationManual.current) {
          setAdCheckPending(false);
          return;
        }

        const response = await fetch('/api/playback/validate-ad', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            clips_played_count: playback.clipsSinceLastAd,
            elapsed_seconds_since_last_ad: playback.elapsedContentSeconds,
          }),
        });

        /* c8 ignore start */
        if (!response.ok) {
          console.error('Ad validation failed:', response.status);
          setAdCheckPending(false);
          return;
        }

        const data = await response.json();
        if (data.should_show_ad) {
          setShouldShowAd(true);
        }
        /* c8 ignore stop */
      } catch (err) {
        console.error('Error checking ad eligibility:', err);
      } finally {
        setAdCheckPending(false);
      }
    }

    checkAndTriggerAd();
  }, [playback.currentIndex, user?.tier?.features?.playback?.ad_enabled, adCheckPending, playback.lastNavigationManual, playback.clipsSinceLastAd, playback.elapsedContentSeconds]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlay = useCallback(async () => {
    const vid = videoRef.current;
    /* c8 ignore start */
    if (vid) {
      /* c8 ignore stop */
      // For HTML5 video elements
      try {
        /* c8 ignore start */
        if (isPlaying) {
          vid.pause();
          setIsPlaying(false);
          if (playback.setPlayState) playback.setPlayState(false);
          analytics.trackPlaybackPause();
        } else {
          const p = vid.play();
          if (p && typeof p.then === 'function') await p;
          setIsPlaying(true);
          if (playback.setPlayState) playback.setPlayState(true);
          analytics.trackPlaybackResume();
        }
        /* c8 ignore stop */
      } catch (e) {
        setIsPlaying((prev) => !prev);
      }
      return;
    }

    // For Twitch embeds, we can't control playback due to cross-origin restrictions.
    // Just update the state to reflect user intent and let auto-advance handle it.
    /* c8 ignore start */
    if (playback.togglePlay) {
      playback.togglePlay();
      setIsPlaying((prev) => !prev);
      if (isPlaying) {
        analytics.trackPlaybackPause();
      } else {
        analytics.trackPlaybackResume();
      }
    }
    /* c8 ignore stop */
  }, [isPlaying, playback]);

  const toggleAutoPlayMode = useCallback(() => {
    setAutoPlayMode((prev) => {
      const next = !prev;
      /* c8 ignore start */
      if (next) {
        // switching to Auto-Play: resume
        setIsPlaying(true);
        if (playback.setPlayState) playback.setPlayState(true);
      }
      /* c8 ignore stop */
      return next;
    });
  }, [playback]);

  // Removed toggleFullscreen — use Twitch embed fullscreen controls instead

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e) {
      /* c8 ignore start */
      if (e.key === ' ' || e.code === 'Space') { 
        e.preventDefault(); 
        togglePlay(); 
      }
      if (e.key === 'ArrowLeft') { 
        e.preventDefault(); 
        playback.playPrevious?.(true);
        analytics.trackPlaybackPrevious();
      }
      if (e.key === 'ArrowRight') { 
        e.preventDefault(); 
        playback.playNext?.(true);
        analytics.trackPlaybackNext();
      }
      /* c8 ignore stop */
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay, playback]);

  const onAdComplete = useCallback((status) => {
    /* c8 ignore start */
    if (status === 'skipped') {
      analytics.trackPlaybackSkipAd(user?.tier?.features?.playback?.ad_skip_after_seconds);
    }
    /* c8 ignore stop */
    playback.resetAdCounters?.();
    setShouldShowAd(false);
  }, [playback, user?.tier?.features?.playback?.ad_skip_after_seconds]);

  const onAdError = useCallback((reason) => {
    analytics.trackPlaybackAdError(reason);
    playback.resetAdCounters?.();
    setShouldShowAd(false);
  }, [playback]);

  const onAdStart = useCallback((adData) => {
    analytics.trackPlaybackAdImpression(adData.adUnitId, adData.durationSeconds);
  }, []);

  const onIframeLoad = useCallback(() => {
    iframeLoadedRef.current = true;
    setEmbedBlocked(false);
    if (iframeTimeoutRef.current) {
      clearTimeout(iframeTimeoutRef.current);
      iframeTimeoutRef.current = null;
    }
    // Start the auto-advance timer now that the embed has actually loaded.
    // This is the accurate start point — not clip-change time which precedes
    // iframe load by 2-5 s (and that gap grows with each clip, compounding drift).
    /* c8 ignore start */
    if (autoPlayDurationMsRef.current !== null && !autoAdvanceRef.current) {
      autoAdvanceRef.current = setTimeout(() => {
        playNextRef.current?.(false);
        setPlayStateRef.current?.(true);
      }, autoPlayDurationMsRef.current);
    }
    /* c8 ignore stop */
  }, []);

  // --- compute embed src outside render (pure derivation, no side effects) ---
  const embedSrc = useMemo(() => {
    const clip = playback.currentClip;
    if (!clip) return null;

    // Extract the clip slug so we always build the correct embed URL:
    //   https://clips.twitch.tv/embed?clip=SLUG&parent=DOMAIN
    // Twitch blocks X-Frame-Options on the regular clip page URL, so we must
    // use the embed endpoint even if embedUrl is the page URL.
    let slug = null;

    if (clip.embedUrl) {
      try {
        const u = new URL(clip.embedUrl);
        // embed_url format from Twitch: https://clips.twitch.tv/embed?clip=SLUG
        slug = u.searchParams.get('clip');
        // fallback: path-based format https://clips.twitch.tv/SLUG
        if (!slug && u.hostname === 'clips.twitch.tv') {
          const p = u.pathname.replace(/^\/+/, '').split('/')[0];
          /* c8 ignore start */
          if (p && p !== 'embed') slug = p;
          /* c8 ignore stop */
        }
      } catch { /* ignore */ }
    }

    if (!slug && clip.url) {
      try {
        const u = new URL(clip.url);
        if (u.hostname === 'clips.twitch.tv') {
          // https://clips.twitch.tv/SLUG
          /* c8 ignore start */
          slug = u.pathname.replace(/^\/+/, '').split('/')[0] || null;
          /* c8 ignore stop */
        } else if (u.hostname === 'www.twitch.tv' || u.hostname === 'twitch.tv') {
          // https://www.twitch.tv/CHANNEL/clip/SLUG
          const m = u.pathname.match(/\/clip\/([^/?#]+)/);
          /* c8 ignore start */
          if (m) slug = m[1];
          /* c8 ignore stop */
        }
      } catch { /* ignore */ }
    }

    if (!slug) return null;

    // Twitch requires `parent` = the exact domain embedding the iframe.
    // Twitch does NOT accept localhost or 127.0.0.1 — it requires a real domain.
    //
    // For LOCAL DEVELOPMENT:
    //   Option A (recommended): Use ngrok to tunnel localhost to a real domain
    //     ngrok http 3000  →  access app at https://abc123.ngrok.io
    //     Set VITE_TWITCH_PARENT=abc123.ngrok.io
    //   Option B: Use a local domain via hosts file
    //     Add "127.0.0.1 clipto.local" to /etc/hosts
    //     Access app at http://clipto.local:3000
    //     Set VITE_TWITCH_PARENT=clipto.local
    //
    // For PRODUCTION:
    //   Set VITE_TWITCH_PARENT to your actual domain (e.g., clipto.tv,www.clipto.tv)
    
    let parent = window.location.hostname;
    
    // If running on localhost/127.0.0.1, use env override (will be empty if not set)
    const envOverride = import.meta.env.VITE_TWITCH_PARENT;
    if (envOverride) {
      parent = envOverride.split(',')[0].trim(); // Use first domain from CSV list
    }
    
    return `https://clips.twitch.tv/embed?clip=${encodeURIComponent(slug)}&parent=${parent}&autoplay=true&muted=false`;
  }, [playback.currentClip?.embedUrl, playback.currentClip?.url]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Gate check (after all hooks) ---
  if (!globalEnabled || !playbackEnabled) {
    return (
      <div className="min-h-screen bg-dark-900 text-white" data-testid="playback-access-denied">
        <main className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-gray-400">Playback is not available for your account or feature flag.</p>
        </main>
      </div>
    );
  }

  // Compute total duration (seconds) across all loaded clips
  const totalDurationSeconds = useMemo(() => {
    const arr = playback.clips || [];
    return arr.reduce((sum, c) => {
      const v = Number(c?.duration);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);
  }, [playback.clips]);

  function formatTotalDuration(seconds) {
    if (!seconds || seconds <= 0) return '0:00';
    if (seconds >= 3600) {
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
      const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
      return `${hrs}:${mins}:${secs}`;
    }
    return formatDuration(seconds);
  }

  return (
    <div
      className="min-h-screen bg-dark-900 text-white flex flex-col"
      ref={containerRef}
      data-testid="playback-page"
    >
      <Header />

      {/* Info bar with channels and return button */}
      <div className="bg-dark-800 border-b border-dark-700">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="text-sm text-gray-300 flex items-center">
            <span className="mr-3">Channels:</span>
            {channels.length > 0 ? (
              <div className="flex items-center -space-x-2">
                {channels.map((ch) => {
                  const slug = (ch || '').toString().trim().toLowerCase();
                  const avatar = avatarForChannelSlug(slug);
                  /* c8 ignore start */
                  const href = slug ? `https://www.twitch.tv/${encodeURIComponent(slug)}` : null;
                  const handleAvatarClick = (e) => { if (!href) e.preventDefault(); };
                  /* c8 ignore stop */
                  /* c8 ignore start */
                  return (
                    <a
                      key={slug || Math.random()}
                      href={href || '#'}
                      target="_blank"
                      rel="noreferrer"
                      title={slug}
                      className="w-6 h-6 rounded-full overflow-hidden ring-2 ring-primary/50 bg-dark-700 border border-dark-600 inline-block"
                      onClick={handleAvatarClick}
                    >
                      <img
                        src={avatar || '/avatar.png'}
                        alt={slug}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/avatar.png'; }}
                      />
                    </a>
                  );
                  /* c8 ignore stop */
                })}
              </div>
            ) : (
              <span className="text-gray-400">All favorites</span>
            )}
          </div>
          <button
            data-testid="close-button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-dark-700 hover:bg-dark-600 rounded text-sm"
          >
            ← Return to site
          </button>
        </div>
      </div>

      {/* Video area */}
      <div className="flex-1 flex items-center justify-center bg-black">
        <div className="relative w-full" style={{ maxWidth: '960px', aspectRatio: '16/9' }}>
          {embedSrc ? (
            <iframe
              key={embedSrc}
              title={clip?.title || 'clip'}
              data-testid="playback-embed"
              src={embedSrc}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              allow="autoplay; fullscreen"
              allowFullScreen
              onLoad={onIframeLoad}
            />
          ) : isMp4 ? (
            <video
              key={clip.url}
              ref={videoRef}
              data-testid="playback-video"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
              src={clip.url}
              controls
              autoPlay
              onEnded={() => playback.playNext(false)}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400">
              {clip ? 'This clip cannot be embedded.' : 'No clip loaded.'}
            </div>
          )}

          {/* c8 ignore start */}
          {embedBlocked && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-yellow-900/80 text-yellow-200 text-sm px-4 py-2 rounded-lg">
              Twitch blocked the embed —
              {clip?.url && (
                <a href={clip.url} target="_blank" rel="noreferrer" className="ml-1 underline">Open on Twitch</a>
              )}
            </div>
          )}
          {/* c8 ignore stop */}
        </div>
      </div>
      {/* Playback controls footer */}
      <div className="bg-dark-800 border-t border-dark-700 px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-dark-700 border border-dark-500 rounded-xl shadow-2xl p-4">
            {/* Clip info and playback controls */}
            <div className="flex items-center justify-between gap-4 mb-3">
              <div className="flex items-center gap-3">
                {(() => {
                  const clipSlug = extractChannelSlug(clip);
                  
                  // Try to build an avatar URL in this order:
                  // 1. Explicit profile_image_url if available
                  // 2. Twitch user avatar via unavatar.io (using extracted broadcaster login)
                  // 3. Fallback to clip thumbnail as avatar
                  // 4. Show initials if nothing else works
                  
                  let avatarUrl = clip?.profile_image_url || clip?.profileImageUrl || null;
                  
                  if (!avatarUrl) {
                    // Extract broadcaster login from clip URL or fields
                    let bestSlug = '';
                    /* c8 ignore start */
                    try {
                      if (clip?.url) {
                        const u = new URL(clip.url);
                        if (u.hostname && (u.hostname.includes('twitch.tv') || u.hostname.includes('clips.twitch.tv'))) {
                          const parts = u.pathname.replace(/^\/+/, '').split('/');
                          if (parts.length > 0 && parts[0] !== 'clip' && parts[0] !== 'embed') {
                            bestSlug = parts[0].toLowerCase();
                          }
                        }
                      }
                    } catch (e) {
                      // ignore URL parsing errors
                    }

                    if (!bestSlug) {
                      const slugCandidates = [
                        clip?.broadcaster_login,
                        clip?.broadcasterLogin,
                        clip?.channel,
                        clip?.channelName,
                        clip?.user_login,
                        clip?.userLogin,
                      ];
                      const best = slugCandidates.find(s => !!s);
                      bestSlug = (best || '').toString().toLowerCase();
                    }

                    if (bestSlug) {
                      // Use unavatar.io for Twitch profile images
                      avatarUrl = `https://unavatar.io/twitch/${encodeURIComponent(bestSlug)}`;
                    } else if (clip?.thumbnailUrl || clip?.thumbnail_url) {
                      // Fallback: use clip thumbnail as avatar (better than nothing)
                      avatarUrl = clip?.thumbnailUrl || clip?.thumbnail_url;
                    }
                    /* c8 ignore stop */
                  }
                  
                  const displayName = (clip?.channel || clip?.broadcasterName || clip?.broadcaster_name || extractChannelSlug(clip) || 'U').toString();
                  const initials = displayName.split(/\s+/).slice(0,2).map(s => s[0]).join('').toUpperCase();
                  const colorHash = Array.from(displayName).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
                  const colors = ['bg-indigo-600','bg-pink-600','bg-purple-600','bg-green-600','bg-yellow-600','bg-red-600'];
                  const bgClass = colors[Math.abs(colorHash) % colors.length];
                  const shouldShowImage = avatarUrl && !failedAvatarUrls.has(avatarUrl);

                  return (
                    <div>
                      <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center ring-2 ring-primary/50 p-0.5 bg-transparent">
                        {shouldShowImage ? (
                          <img
                            src={avatarUrl}
                            alt={displayName}
                            className="w-full h-full object-cover"
                            onError={() => {
                              console.warn('[PlaybackPage] Avatar failed:', avatarUrl);
                              setFailedAvatarUrls(prev => new Set([...prev, avatarUrl]));
                            }}
                          />
                        ) : (
                          <div className={`w-full h-full flex items-center justify-center text-white font-bold text-sm ${bgClass}`}>
                            {initials}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
                <div>
                  {clip ? (
                    <>
                      <p className="font-medium text-white text-sm">
                        {/* c8 ignore start */}
                        {(() => {
                          const streamerSlug = (clip?.broadcaster_login || clip?.broadcasterLogin || clip?.channel || clip?.channelName || extractChannelSlug(clip) || '').toString().toLowerCase();
                          const streamerUrl = streamerSlug ? `https://www.twitch.tv/${encodeURIComponent(streamerSlug)}` : null;
                          const displayName = clip?.channel || clip?.broadcasterName || clip?.broadcaster_name || clip?.channelName || streamerSlug || 'Unknown channel';
                          return streamerUrl ? (
                            <a href={streamerUrl} target="_blank" rel="noreferrer" className="hover:underline">
                              {displayName}
                            </a>
                          ) : (
                            displayName
                          );
                        })()}
                        {/* c8 ignore stop */} {
                          ` — ${clip?.title || ''}`
                        }
                      </p>

                      <p className="text-xs text-gray-400">
                        {/* c8 ignore start */}
                        {clip?.creatorName && (() => {
                          const creatorCandidate = (clip?.creator_login || clip?.creatorLogin || clip?.creatorName || clip?.creator_name || '').toString();
                          const creatorSlug = creatorCandidate ? creatorCandidate.toLowerCase().replace(/\s+/g, '') : '';
                          const creatorUrl = creatorSlug ? `https://www.twitch.tv/${encodeURIComponent(creatorSlug)}` : null;
                          return creatorUrl ? (
                            <>
                              <a href={creatorUrl} target="_blank" rel="noreferrer" className="text-gray-300 hover:underline">Clipped by {clip.creatorName}</a>
                            </>
                          ) : (
                            <>Clipped by {clip.creatorName}</>
                          );
                        })()}
                        {/* c8 ignore stop */}
                        {clip?.creatorName && (clip?.clipCreatedAt || clip?.created_at) && ' • '}
                        { (clip?.clipCreatedAt || clip?.created_at) && formatDate(clip?.clipCreatedAt || clip?.created_at, 'date') }
                      </p>

                      <p className="text-xs text-gray-500">
                        Clip {Math.min(playback.currentIndex + 1, playback.clips.length)} / {playback.clips.length}
                        {playback.clips && playback.clips.length > 0 && (
                          <span className="mx-2">•</span>
                        )}
                        {playback.clips && playback.clips.length > 0 && (
                          <span className="text-xs text-gray-400">Total {formatTotalDuration(totalDurationSeconds)}</span>
                        )}
                      </p>
                    </>
                  ) : (
                    <p className="font-medium text-white text-sm">No clip</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Open / Copy / Add moved to left of playback controls */}
                <div className="flex items-center gap-2">
                  <button
                    data-testid="open-button"
                    onClick={() => {
                      /* c8 ignore start */
                      if (!clip || !clip.url) return;
                      try {
                        window.open(clip.url, '_blank', 'noopener');
                      } catch (e) {
                        // fallback
                        const a = document.createElement('a');
                        a.href = clip.url;
                        a.target = '_blank';
                        a.rel = 'noopener';
                        a.click();
                      }
                      /* c8 ignore stop */
                    }}
                    className="px-3 py-2 bg-dark-700 hover:bg-dark-600 rounded text-sm font-medium inline-flex items-center"
                    title="Open on Twitch"
                  >
                    <span aria-hidden="true" className="mr-1">↗</span>
                    <span>Open</span>
                  </button>

                  <button
                    data-testid="copy-button"
                    onClick={async () => {
                      /* c8 ignore start */
                      if (!clip || !clip.url) return;
                      if (copying) return;
                      /* c8 ignore stop */
                      setCopying(true);
                      setCopied(false);
                      try {
                        /* c8 ignore start */
                        if (navigator.clipboard && navigator.clipboard.writeText) {
                          await navigator.clipboard.writeText(clip.url);
                        } else {
                          const ta = document.createElement('textarea');
                          ta.value = clip.url;
                          ta.style.position = 'fixed';
                          ta.style.opacity = '0';
                          document.body.appendChild(ta);
                          ta.select();
                          document.execCommand('copy');
                          document.body.removeChild(ta);
                        }
                        /* c8 ignore stop */
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      } catch (err) {
                        // ignore copy failures
                      } finally {
                        setCopying(false);
                      }
                    }}
                    className="px-3 py-2 bg-dark-700 hover:bg-dark-600 rounded text-sm font-medium inline-flex items-center"
                    title="Copy clip URL"
                  >
                    <span aria-hidden="true" className="mr-1">🔗</span>
                    <span>{copying ? 'Copying…' : copied ? 'Copied' : 'Copy'}</span>
                  </button>

                  <button
                    data-testid="add-to-collection"
                    onClick={async () => {
                      /* c8 ignore start */
                      if (!clip || adding || added) return;
                      /* c8 ignore stop */
                      setAddError(null);
                      setAdding(true);
                      try {
                        const clipData = {
                          twitch_clip_id: clip.id,
                          /* c8 ignore start */
                          broadcaster_id: clip.broadcaster_id || clip.broadcasterId || '',
                          broadcaster_name: clip.channel || clip.broadcaster_name || clip.broadcasterName || '',
                          broadcaster_login: clip.broadcaster_login || clip.broadcasterLogin || '',
                          title: clip.title || '',
                          url: clip.url || clip.embedUrl || '',
                          thumbnail_url: clip.thumbnail_url || clip.thumbnailUrl || null,
                          view_count: clip.view_count ?? clip.viewCount ?? 0,
                          duration: clip.duration ?? 0,
                          clip_created_at: clip.created_at || clip.createdAt || null,
                          creator_name: clip.creatorName || null,
                          /* c8 ignore stop */
                        };

                        await addToCollection(clipData);
                        setAdded(true);
                        // auto-clear success after a short time
                        setTimeout(() => setAdded(false), 2500);
                      } catch (err) {
                        setAddError(err.message || 'Failed to add');
                      } finally {
                        setAdding(false);
                      }
                    }}
                    className="px-3 py-2 bg-dark-700 hover:bg-dark-600 rounded text-sm font-medium"
                    title="Add to Collection"
                    disabled={adding || added}
                  >
                    {adding ? 'Adding…' : added ? 'Added' : '☆ Add'}
                  </button>
                </div>

                {/* Playback controls */}
                <button
                  data-testid="prev-button"
                  onClick={() => {
                    playback.playPrevious?.(true);
                    analytics.trackPlaybackPrevious();
                  }}
                  className="px-3 py-2 bg-dark-700 hover:bg-dark-600 rounded text-sm font-medium"
                  title="Previous clip (←)"
                >
                  ⏮
                </button>
                <button
                  data-testid="mode-toggle-button"
                  onClick={toggleAutoPlayMode}
                  className="px-4 py-2 bg-primary hover:bg-primary-dark rounded font-medium text-sm"
                  title={autoPlayMode ? 'Switch to Manual Advance' : 'Switch to Auto-Play'}
                >
                  {autoPlayMode ? '🔄 Auto-Play' : '⏭️ Manual Advance'}
                </button>
                <button
                  data-testid="play-toggle"
                  onClick={togglePlay}
                  className="px-4 py-2 bg-dark-600 hover:bg-dark-500 rounded font-medium text-sm"
                  title={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? 'Pause' : 'Play'}
                </button>
                <button
                  data-testid="next-button"
                  onClick={() => {
                    playback.playNext?.(true);
                    analytics.trackPlaybackNext();
                  }}
                  className="px-3 py-2 bg-dark-700 hover:bg-dark-600 rounded text-sm font-medium"
                  title="Next clip (→)"
                >
                  ⏭
                </button>
              </div>
            </div>

            {/* Keyboard shortcuts help */}
            <div className="flex items-center gap-2 pt-3 border-t border-dark-500">
              <span className="text-xs text-gray-400">Shortcuts:</span>
              <button className="px-2 py-1 text-xs bg-dark-600 hover:bg-dark-500 rounded text-gray-300 cursor-default">←/→</button>
              <span className="text-xs text-gray-400">Prev/Next</span>
              {/* Fullscreen handled by embedded Twitch player */}
            </div>
            {addError && (
              <div className="mt-2 text-sm text-red-400">{addError}</div>
            )}
          </div>
        </div>
      </div>

      {shouldShowAd && (
        <AdOverlay
          adUnitId={user?.tier?.features?.playback?.ad_unit_id}
          preferredDuration={user?.tier?.features?.playback?.ad_preferred_duration}
          maxDurationSeconds={user?.tier?.features?.playback?.ad_max_duration_seconds}
          skippable={user?.tier?.features?.playback?.ad_skippable}
          skipAfterSeconds={user?.tier?.features?.playback?.ad_skip_after_seconds}
          onComplete={onAdComplete}
          onError={onAdError}
          onStart={onAdStart}
        />
      )}
    </div>
  );
}



